import { Alert, Button, Gap, Heading, Input } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAllowListModal } from '../CampaignWorkflowTab/contexts';
import { useCampaignSettings } from '../CampaignWorkflowTab/state/CampaignSettings/hooks/useCampaignSettings';
import { useGetEditOrActual } from '../CampaignWorkflowTab/state/CampaignSettings/selectors/useGetEditOrActual';
import { WorkflowBanner } from '../CampaignWorkflowTab/components/Canvas/components/WorkflowBanner/WorkflowBanner';
import { OnsiteLookBackPeriod } from './components/OnsiteLookBackPeriod';
import { useFeatureFlags } from 'core/app/AppContext';

const FlexCenter = styled.div`
  max-width: 600px;
  padding: 32px 50px 64px 50px;
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  h2 {
    margin-top: 32px;
    margin-bottom: 24px;
  }
`;
const NotifText = styled.div`
  font-weight: 350;
  font-size: 14px;
  line-height: 24px;
  margin: 24px 0;
`;

const ButtonContainer = styled.div`
  display: flex;
`;

export function CampaignSettingsTab() {
  const { t } = useTranslation('campaigns');
  const { flagEnabled } = useFeatureFlags();
  const state = useGetEditOrActual();
  const { openAllowListModal } = useAllowListModal();
  const [campaignInfo, setCampaignInfo] = useState(state);
  const hasError = false; //we need this inn future for handel error
  const { name: campaignName, status: campaignStatus, allowList, setName } = useCampaignSettings();

  useEffect(() => {
    setCampaignInfo(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const campaignNameValidationState: { message: string; status: 'warning' | 'complete' | '' } = useMemo(() => {
    const minAllowedChar = 5;
    const maxAllowedChar = 100;

    if ((campaignName?.length > 0 && campaignName?.length < minAllowedChar) || campaignName?.length > maxAllowedChar) {
      return {
        message: 'Name should be between 5 - 100 characters',
        status: 'warning',
      };
    } else if (campaignName === null || campaignName === '') {
      return {
        message: '',
        status: '',
      };
    }

    return {
      message: null,
      status: 'complete',
    };
  }, [campaignName]);

  const onOpenIPWhiteListModal = () => {
    openAllowListModal();
  };

  return (
    <>
      <FlexCenter>
        {hasError && (
          <div
            style={{
              marginTop: '32px',
            }}
          >
            <Alert heading="This is an error alert" variation="error">
              Users are being shown messages to collect permission for marketing emails, however we are ignoring this
              data when sending emails.
            </Alert>
          </div>
        )}
        <WorkflowBanner container={children => <div style={{ marginTop: 32 }}>{children}</div>} />
        <Heading variation="h2">General</Heading>
        <Input
          type="text"
          placeholder={t('onsite.campaignName.placeholder')}
          value={campaignName}
          onChange={setName}
          ariaLabel={t('onsite.campaignName.label')}
          label={t('onsite.campaignName.label')}
          hint="Name should be between 5 - 100 characters"
          hasCharCount={true}
          charLength={campaignName.length}
          isBottomCharCount={true}
          disabled={(campaignStatus === 'Test' || campaignStatus === 'Live') && allowList?.length > 0}
          maxLength={100}
          validationState={campaignNameValidationState.status}
        />
        {flagEnabled('ld-trends') && <OnsiteLookBackPeriod />}
        <Heading variation="h2">Testing</Heading>
        <Alert
          variation="info"
          heading="When your campaign is in test mode, the settings below will override any audience selections in your
          campaign flow. Once your campaign is published, test settings will not apply."
        />
        {campaignInfo?.allowList?.length > 0 ? (
          <>
            <NotifText>
              {campaignInfo.allowList.length === 1
                ? t('onsite.campaignSettingsTab.allowList.IP', {
                    numberOfIPs: campaignInfo.allowList.length,
                  })
                : t('onsite.campaignSettingsTab.allowList.IPs', {
                    numberOfIPs: campaignInfo.allowList.length,
                  })}
            </NotifText>
            <ButtonContainer>
              <Button
                onClick={onOpenIPWhiteListModal}
                variation="secondary"
                ariaLabel={t('onsite.campaignSettingsTab.allowList.edit.label')}
                title={t('onsite.campaignSettingsTab.allowList.edit.title')}
                disabled={(campaignStatus === 'Test' || campaignStatus === 'Live') && allowList?.length > 0}
              />
            </ButtonContainer>
          </>
        ) : (
          <>
            <NotifText>{t('onsite.campaignSettingsTab.allowList.noIPText')}</NotifText>
            <ButtonContainer>
              <Button
                onClick={onOpenIPWhiteListModal}
                variation="secondary"
                ariaLabel={t('onsite.campaignSettingsTab.allowList.add.label')}
                title={t('onsite.campaignSettingsTab.allowList.add.label')}
              />
            </ButtonContainer>
          </>
        )}
        <Gap bottom={32} />
        {/* <EmailAllowListMultiSelects /> We will need this feature in the future, so we should hide it for now  */}
      </FlexCenter>
    </>
  );
}
